/**
 * 明细抽屉的筛选口径。
 *
 * - 包牌明细：包牌列表随看板主数据整份下发，抽屉里直接用 filterBags 过滤，过滤结果即全量
 * - 包裹明细：抽屉只加载停留最久的前 N 个，筛选必须交给后端才能覆盖整格；
 *   筛选条件经 toDetailQuery 放进 DetailQuery，mock 阶段由 buildMockDetails 调 filterParcels 模拟后端
 */
import type { BagRecord, BagResult, DetailQuery, ParcelDetail } from '../types';

/** 批量单号输入上限，与 TT 异常单页一致 */
export const MAX_NUMBER_FILTER_COUNT = 100;

export interface ParcelFilter {
    trackingNumbers: string[];
    flowPrefix: string;
    bagNumber: string;
    /** '' = 不筛；页面已选客户时抽屉不出现该项 */
    customer: string;
}

export const EMPTY_PARCEL_FILTER: ParcelFilter = { trackingNumbers: [], flowPrefix: '', bagNumber: '', customer: '' };

export interface BagFilter {
    bagNumbers: string[];
    flowPrefix: string;
    carriers: string[];
    results: BagResult[];
}

export const EMPTY_BAG_FILTER: BagFilter = { bagNumbers: [], flowPrefix: '', carriers: [], results: [] };

/** 批量粘贴的单号：去首尾空白、去空串、去重，保持输入顺序 */
export function normalizeNumbers(values: string[]): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const v of values) {
        const s = v.trim();
        if (!s || seen.has(s)) continue;
        seen.add(s);
        out.push(s);
    }
    return out;
}

/** 三段码前缀匹配，不区分大小写；前缀为空视为不筛 */
export function matchFlowPrefix(flow: string | null | undefined, prefix: string | undefined): boolean {
    const p = (prefix ?? '').trim().toUpperCase();
    if (!p) return true;
    return (flow ?? '').toUpperCase().startsWith(p);
}

export function hasParcelFilter(f: ParcelFilter): boolean {
    return f.trackingNumbers.length > 0 || !!f.flowPrefix.trim() || !!f.bagNumber.trim() || !!f.customer;
}

export function hasBagFilter(f: BagFilter): boolean {
    return f.bagNumbers.length > 0 || !!f.flowPrefix.trim() || f.carriers.length > 0 || f.results.length > 0;
}

/** 在打开抽屉时的查询上叠加抽屉内的筛选；页面已选客户时以页面为准 */
export function toDetailQuery(base: DetailQuery, f: ParcelFilter): DetailQuery {
    return {
        ...base,
        customer: base.customer ?? (f.customer || undefined),
        trackingNumbers: f.trackingNumbers.length > 0 ? f.trackingNumbers : undefined,
        flowPrefix: f.flowPrefix.trim() || undefined,
        bagNumber: f.bagNumber.trim() || undefined
    };
}

/** 包裹明细的筛选口径（客户在查询层处理，不在这里）。后端照此实现，mock 用它模拟 */
export function filterParcels(
    rows: ParcelDetail[],
    q: Pick<DetailQuery, 'trackingNumbers' | 'flowPrefix' | 'bagNumber'>
): ParcelDetail[] {
    const tns = q.trackingNumbers && q.trackingNumbers.length > 0 ? new Set(q.trackingNumbers) : null;
    const bag = (q.bagNumber ?? '').trim();
    return rows.filter(
        (r) =>
            (!tns || tns.has(r.trackingNumber)) && matchFlowPrefix(r.flow, q.flowPrefix) && (!bag || r.bagNumber === bag)
    );
}

export function filterBags(rows: BagRecord[], f: BagFilter): BagRecord[] {
    const nums = f.bagNumbers.length > 0 ? new Set(f.bagNumbers) : null;
    const carriers = f.carriers.length > 0 ? new Set(f.carriers) : null;
    const results = f.results.length > 0 ? new Set<BagResult>(f.results) : null;
    return rows.filter(
        (r) =>
            (!nums || nums.has(r.bagNumber)) &&
            matchFlowPrefix(r.sortingRule, f.flowPrefix) &&
            (!carriers || (r.carrierName !== null && carriers.has(r.carrierName))) &&
            (!results || results.has(r.bagResult))
    );
}
